import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid2'
import { teamAPI } from 'api/TeamAPI'
import ErrorOverlay from 'Components/General/ErrorOverlay'
import { getTeamInitialData } from 'Components/reusable/DataContext/InitialData'
import { teamSchema } from 'Components/reusable/DataContext/ValidationSchema'
import { useUser } from 'hooks'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IError } from 'types/error'
import { ITeam } from 'types/teamInterfaces'
import TeamCard from './TeamCard'
import TeamModal from './TeamModal'
import TeamViewPage from './TeamViewPage'
import CardContentText from 'Components/reusable/CaleoCustomComponents/CardContentText'
import { personAPI } from 'api/PersonAPI'
import { permissionAPI } from 'api/PermissionAPI'
import { orderBy } from 'lodash'
import { ISkill, IRole, IIndustry } from 'types/levelItemInterfaces'
import FilterFields from './FilterFields'
import colors from 'constants/colors'
import AddIcon from '@mui/icons-material/Add'
import { useLocation, useNavigate, useParams } from 'react-router'
import { ICertificate, ICompetence } from 'types/cvsInterfaces'

/**
 * Renders the Team component.
 *
 * @return {React.ReactElement} The rendered Team component.
 * @notExported
 */
const Team = () => {
  const { user, groups } = useUser()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { id } = useParams()
  const [editedItem, setEditedItem] = useState<ITeam | null | undefined>()
  const [teams, setTeams] = useState<ITeam[]>([])
  const [backendError, setBackendError] = useState<IError>()
  const [viewItem, setViewItem] = useState<ITeam | undefined>()
  const [loading, setLoading] = useState<boolean>(true)
  const [allTeams, setAllTeams] = useState<boolean>(false)
  const [images, setImages] = useState<{ accountId: number; image: string }[]>([])
  const [leaderAccess, setLeaderAccess] = useState<number[]>([])
  const [skillFilter, setSkillFilter] = useState<ISkill[]>([])
  const [roleFilter, setRoleFilter] = useState<IRole[]>([])
  const [industryFilter, setIndustryFilter] = useState<IIndustry[]>([])
  const [certificateFilter, setCertificateFilter] = useState<ICertificate[]>([])
  const [competenceFilter, setCompetenceFilter] = useState<ICompetence[]>([])
  const [loadingFilters, setLoadingFilters] = useState<boolean>(false)
  const [filtered, setFiltered] = useState<ITeam[]>([])

  const adminOrSalesAccount = groups.includes('sales') || groups.includes('companyAdmin') || groups.includes('admin')
  const isTeamModalRoute = location.pathname === '/teams/add' || (!!id && location.pathname === `/teams/edit/${id}`)

  useEffect(() => {
    if (id && location.pathname === `/teams/${id}`) {
      const item = teams.find(team => team.id === parseInt(id))
      if (item) {
        setViewItem(prev => (prev?.id === item.id ? prev : item))
      }
    } else if (id && location.pathname === `/teams/edit/${id}`) {
      const item = teams.find(team => team.id === parseInt(id))
      if (item) {
        setEditedItem(prev => (prev?.id === item.id ? prev : item))
      }
    } else if (!id && location.pathname === '/teams/add' && adminOrSalesAccount) {
      setEditedItem(prev => (prev === null ? prev : null))
    } else if (location.pathname !== '/teams') {
      navigate('/teams', { replace: true })
    }
  }, [adminOrSalesAccount, id, location.pathname, navigate, teams])

  useEffect(() => {
    const controller = new AbortController()

    ;(async () => {
      if (user && groups) {
        try {
          let teamsData
          setLoading(true)
          if (groups.includes('admin') && allTeams) {
            teamsData = await teamAPI.getAllTeams(controller)
          } else if (groups.includes('sales') || groups.includes('companyAdmin')) {
            teamsData = await teamAPI.getSalesTeams(controller)
          } else {
            teamsData = await teamAPI.getUserTeams(controller)
          }
          setTeams(orderBy(teamsData, 'organizationId', 'asc'))
        } catch (err) {
          setBackendError(err as IError)
        } finally {
          setLoading(false)
        }
      }
    })()

    return () => {
      controller.abort()
    }
  }, [groups, user, allTeams])

  useEffect(() => {
    const controller = new AbortController()

    ;(async () => {
      const imagesMap: { accountId: number; image: string }[] = []
      for (const team of teams) {
        for (const member of team.TeamMembers) {
          if (
            member.Account &&
            member.Account.Person &&
            member.hasAccess &&
            !imagesMap.find(image => image.accountId === member.Account.id)
          ) {
            const image = await personAPI.getProfileImage(member.Account.Person.id, controller)
            imagesMap.push({ accountId: member.Account.id, image: image ?? '' })
          }
        }
      }
      setImages(imagesMap)
      if (teams.length > 0) {
        const results = await permissionAPI.getLeaderAccessToTeams(
          teams.map(team => team.id),
          controller
        )
        setLeaderAccess(results)
      }
    })()

    return () => {
      controller.abort()
    }
  }, [teams])

  useEffect(() => {
    const controller = new AbortController()

    ;(async () => {
      try {
        setLoadingFilters(true)
        if (teams.length) {
          const results = await teamAPI.filterTeams(
            teams.map(item => item.id),
            skillFilter,
            roleFilter,
            industryFilter,
            certificateFilter,
            competenceFilter,
            controller
          )
          setFiltered(results)
        }
      } catch (error) {
        setBackendError(error as IError)
      } finally {
        setLoadingFilters(false)
      }
    })()

    return () => {
      controller.abort()
    }
  }, [teams, skillFilter, roleFilter, industryFilter, certificateFilter, competenceFilter])

  const checkTeams = () => {
    if (!loading && teams.length === 0) {
      return (
        <Box p={{ md: 3, sm: 1, xs: 0 }} mt={{ xs: 1 }}>
          <CardContentText>{t('team.noTeam')}</CardContentText>
        </Box>
      )
    }
  }

  const replaceOrAddItem = (newItem: ITeam | { deleted: number }) => {
    const newTeams = [...teams]
    if ('id' in newItem && newItem.id) {
      const index = newTeams.findIndex(team => team.id === newItem.id)

      if (index > -1) {
        newTeams[index] = newItem
      } else {
        newTeams.push(newItem)
      }

      if (viewItem) {
        setViewItem(newItem)
      }
    }
    if ('deleted' in newItem && newItem.deleted) {
      const index = newTeams.findIndex(team => team.id === newItem.deleted)

      if (index > -1) {
        newTeams.splice(index, 1)
      }

      if (viewItem) {
        setViewItem(undefined)
      }
    }

    setTeams(newTeams)
  }

  if (backendError && backendError.name !== 'CanceledError' && backendError.name !== 'AbortError') {
    return <ErrorOverlay error={backendError} setOpen={setBackendError} />
  }

  return (
    <Box p={{ md: 3, sm: 1, xs: 0 }} sx={{ background: colors.searchBackground, margin: '0 auto', maxWidth: 1400 }}>
      {!viewItem && (
        <Grid container spacing={2}>
          <Grid container justifyContent="space-between" size={12}>
            <Grid size="auto">
              <Typography variant="h1" gutterBottom fontWeight="bold">
                {t('team.title')}
              </Typography>
            </Grid>
            {adminOrSalesAccount && (
              <Grid size="auto">
                <Button
                  data-testid="button-team-add"
                  startIcon={<AddIcon />}
                  variant="contained"
                  color="primary"
                  onClick={() => navigate('/teams/add', { replace: true })}
                >
                  {t('team.modal.addTitle')}
                </Button>
              </Grid>
            )}
          </Grid>
          {(loading || loadingFilters) && (
            <Grid container justifyContent="center" alignItems="center">
              <Grid size={1}>
                <CircularProgress />
              </Grid>
            </Grid>
          )}
          {checkTeams()}
          {(groups.includes('admin') ||
            groups.includes('companyAdmin') ||
            groups.includes('sales') ||
            groups.includes('teamLeader')) && (
            <FilterFields
              setSkillFilter={setSkillFilter}
              setRoleFilter={setRoleFilter}
              setIndustryFilter={setIndustryFilter}
              setCertificateFilter={setCertificateFilter}
              setCompetenceFilter={setCompetenceFilter}
              allItems={allTeams}
              setAllItems={setAllTeams}
            />
          )}
          <Grid size={12}>
            <Typography variant="h5" fontWeight="bold">
              {t('team.listHeader')}
            </Typography>
          </Grid>
          <Grid container direction="row" spacing={2} size={12}>
            {filtered &&
              filtered.map((team: ITeam) => (
                <TeamCard
                  key={team.id}
                  item={team}
                  images={images}
                  setEditedItem={team => {
                    navigate(`/teams/edit/${team.id}`, { replace: true })
                    setEditedItem(team)
                  }}
                  adminAccount={adminOrSalesAccount}
                  managerAccess={!!leaderAccess.find(id => team.id === id)}
                  setViewItem={team => {
                    navigate(`/teams/${team.id}`, { replace: true })
                    setViewItem(team)
                  }}
                />
              ))}
          </Grid>
        </Grid>
      )}
      {viewItem && (
        <TeamViewPage
          key={viewItem.id}
          item={viewItem}
          adminAccount={adminOrSalesAccount}
          managerAccess={!!leaderAccess.find(id => viewItem.id === id)}
          onClose={() => {
            setViewItem(undefined)
            navigate('/teams', { replace: true })
          }}
          setEdit={item => {
            setEditedItem(item)
            navigate(`/teams/edit/${item.id}`, { replace: true })
          }}
        />
      )}
      {editedItem !== undefined && isTeamModalRoute && (
        <TeamModal
          item={editedItem}
          maxWidth="md"
          initialData={getTeamInitialData(user?.organizationId ?? 0)}
          fullWidth={true}
          localeBase="team.modal"
          schema={teamSchema()}
          submitOnModal={true}
          onClose={(value?: Record<string, unknown> | { id?: number; name?: string } | { newItem?: unknown }) => {
            const newItem = (value as { newItem?: unknown } | undefined)?.newItem as
              | ITeam
              | { deleted: number }
              | undefined
            if (newItem) {
              replaceOrAddItem(newItem)
            }
            if (viewItem) {
              navigate(`/teams/${viewItem?.id}`, { replace: true })
            } else {
              navigate('/teams', { replace: true })
            }
            setEditedItem(undefined)
          }}
          header="teams"
          noEscClose
          submitColor="primary"
        />
      )}
    </Box>
  )
}

export default Team
